import React from 'react';
import { Box, Button, ButtonBase, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { EastRounded, FilterAltOutlined, ForumOutlined, HighlightOffOutlined } from '@mui/icons-material';
import { TABLE_FONT_FAMILY } from '../../theme';
import { OpportunityPipelineStatus } from '../../types';
import { OPPORTUNITY_STATUS_LABELS } from '../../utils/commercialPlanning';
import { CommercialDashboard, LinearStage, formatEuro, formatRate } from '../../utils/commercialDashboard';
import { ConsolePanel } from './ConsolePanel';
import { NAUTILUS, STAGE_RAMP, inkOn, useConsolePalette } from './consolePalette';

const POINT = 14;
const OVERLAP = 12;

const chevronClip = (index: number, total: number) => {
  const notch = index === 0 ? '' : `, ${POINT}px 50%`;
  if (index === total - 1) {
    return `polygon(0 0, 100% 0, 100% 100%, 0 100%${notch})`;
  }
  return `polygon(0 0, calc(100% - ${POINT}px) 0, 100% 50%, calc(100% - ${POINT}px) 100%, 0 100%${notch})`;
};

type RailCell = {
  key: LinearStage | 'won';
  label: string;
  count: number;
  value: number;
  tooltip: string;
};

type PipelineRailProps = {
  dashboard: CommercialDashboard;
  onOpenLeads: (status?: OpportunityPipelineStatus) => void;
};

const PipelineRail: React.FC<PipelineRailProps> = ({ dashboard, onOpenLeads }) => {
  const palette = useConsolePalette();
  const { funnel, period } = dashboard;

  const cells: RailCell[] = [
    ...funnel.stages.map((stage) => ({
      key: stage.status,
      label: OPPORTUNITY_STATUS_LABELS[stage.status],
      count: stage.reached,
      value: stage.value,
      tooltip: `${stage.reached} reached ${OPPORTUNITY_STATUS_LABELS[stage.status]} · ${stage.count} still here, ${formatEuro(stage.value)}, forecast ${formatEuro(stage.forecast)}`,
    })),
    {
      key: 'won' as const,
      label: OPPORTUNITY_STATUS_LABELS.won,
      count: funnel.outcomes.won.count,
      value: funnel.outcomes.won.value,
      tooltip: `${funnel.outcomes.won.count} won in ${period.label} · ${formatEuro(funnel.outcomes.won.value)}`,
    },
  ];

  const conversionTooltip = (index: number) => {
    const conversion = funnel.conversions[index];
    if (!conversion) return '';
    const stageLabel = OPPORTUNITY_STATUS_LABELS[conversion.from];
    if (conversion.rate == null) {
      return `No lead reached ${stageLabel} yet.`;
    }
    const stillHere = conversion.reached - conversion.advanced - conversion.closed_here;
    return `Of ${conversion.reached} leads that reached ${stageLabel}, ${conversion.advanced} went on to `
      + `${OPPORTUNITY_STATUS_LABELS[conversion.to]}, ${stillHere} are still in ${stageLabel} `
      + `and ${conversion.closed_here} were lost or closed there.`;
  };

  const outcomeTiles = [
    {
      key: 'lost' as const,
      label: 'Lost',
      icon: <HighlightOffOutlined fontSize="small" />,
      accent: NAUTILUS.buoy,
      ink: palette.isDark ? '#f0a35e' : '#b35a10',
    },
    {
      key: 'other' as const,
      label: 'Other',
      icon: <ForumOutlined fontSize="small" />,
      accent: NAUTILUS.slate,
      ink: palette.inkSoft,
    },
  ];

  return (
    <ConsolePanel
      title="Sales pipeline"
      subtitle={`Leads open today or closed in ${period.label}, counted at every stage they reached`}
      icon={<FilterAltOutlined />}
      action={(
        <Button
          size="small"
          onClick={() => onOpenLeads()}
          endIcon={<EastRounded fontSize="small" />}
          sx={{ color: palette.inkSoft, textTransform: 'none', fontWeight: 600 }}
        >
          Leads
        </Button>
      )}
      bodySx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
    >
      <Box sx={{ overflowX: 'auto', pb: 0.5 }}>
        <Box sx={{ minWidth: 560 }}>
          <Box sx={{ display: 'flex' }} role="list" aria-label="Pipeline stages">
            {cells.map((cell, index) => {
              const fill = STAGE_RAMP[cell.key];
              const ink = inkOn(fill);
              return (
                <Box
                  key={cell.key}
                  role="listitem"
                  sx={{
                    flex: '1 1 0',
                    minWidth: 0,
                    ml: index === 0 ? 0 : `-${OVERLAP}px`,
                    pointerEvents: 'none',
                  }}
                >
                  <Tooltip
                    title={`${cell.tooltip}. Open leads currently in ${cell.label}.`}
                    arrow
                  >
                    <ButtonBase
                      onClick={() => onOpenLeads(cell.key)}
                      aria-label={`${cell.label}, ${cell.count} leads`}
                      sx={{
                        width: '100%',
                        height: 72,
                        pointerEvents: 'auto',
                        clipPath: chevronClip(index, cells.length),
                        bgcolor: fill,
                        color: ink,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        pl: index === 0 ? 1 : `${POINT + 6}px`,
                        pr: index === cells.length - 1 ? 1 : `${POINT + 4}px`,
                        cursor: 'pointer',
                        transition: 'filter 150ms ease-out',
                        '&:hover': { filter: 'brightness(1.12)', zIndex: 1 },
                        '&.Mui-focusVisible': {
                          zIndex: 1,
                          boxShadow: `inset 0 0 0 2px ${ink}`,
                        },
                      }}
                    >
                      <Typography
                        noWrap
                        sx={{ fontSize: '0.72rem', fontWeight: 600, letterSpacing: '0.03em', color: alpha(ink, 0.85), maxWidth: '100%' }}
                      >
                        {cell.label}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: TABLE_FONT_FAMILY,
                          fontWeight: 700,
                          fontSize: '1.45rem',
                          lineHeight: 1.1,
                          color: ink,
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {cell.count}
                      </Typography>
                    </ButtonBase>
                  </Tooltip>
                </Box>
              );
            })}
          </Box>

          <Box sx={{ display: 'flex', mt: 0.75 }} aria-label="Stage conversion">
            {cells.map((cell, index) => {
              const conversion = index < funnel.conversions.length ? funnel.conversions[index] : null;
              return (
                <Box
                  key={cell.key}
                  sx={{
                    flex: '1 1 0',
                    minWidth: 0,
                    ml: index === 0 ? 0 : `-${OVERLAP}px`,
                    display: 'flex',
                    justifyContent: 'flex-end',
                    pr: '2px',
                  }}
                >
                  {conversion && (
                    <Tooltip title={conversionTooltip(index)} arrow>
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.25,
                          color: conversion.rate == null ? palette.inkFaint : palette.inkSoft,
                          transform: `translateX(${OVERLAP / 2}px)`,
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{ fontFamily: TABLE_FONT_FAMILY, fontWeight: 600, color: 'inherit' }}
                        >
                          {formatRate(conversion.rate)}
                        </Typography>
                        <EastRounded sx={{ fontSize: 13 }} />
                      </Box>
                    </Tooltip>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
        {outcomeTiles.map((tile) => {
          const outcome = funnel.outcomes[tile.key];
          return (
            <Tooltip
              key={tile.key}
              title={`${outcome.count} ${tile.label.toLowerCase()} in ${period.label} · ${formatEuro(outcome.value)}. Open leads currently in ${tile.label}.`}
              arrow
            >
              <ButtonBase
                onClick={() => onOpenLeads(tile.key)}
                aria-label={`${tile.label}, ${outcome.count} leads`}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  px: 1.5,
                  py: 1.25,
                  borderRadius: 1,
                  border: `1px solid ${alpha(tile.accent, palette.isDark ? 0.45 : 0.35)}`,
                  bgcolor: alpha(tile.accent, palette.isDark ? 0.08 : 0.05),
                  textAlign: 'left',
                  cursor: 'pointer',
                  transition: 'background-color 150ms ease-out',
                  '&:hover': { bgcolor: alpha(tile.accent, palette.isDark ? 0.16 : 0.12) },
                  '&.Mui-focusVisible': { outline: `2px solid ${palette.figure}`, outlineOffset: 2 },
                }}
              >
                <Box sx={{ color: tile.ink, display: 'flex' }} aria-hidden>{tile.icon}</Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: tile.ink }}>
                    {tile.label}
                  </Typography>
                  <Typography variant="caption" sx={{ color: palette.inkFaint }}>
                    Closed in {period.label}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography
                    sx={{ fontFamily: TABLE_FONT_FAMILY, fontWeight: 700, fontSize: '1.2rem', lineHeight: 1.1, color: palette.ink }}
                  >
                    {outcome.count}
                  </Typography>
                  <Typography variant="caption" sx={{ fontFamily: TABLE_FONT_FAMILY, color: palette.inkFaint }}>
                    {formatEuro(outcome.value)}
                  </Typography>
                </Box>
              </ButtonBase>
            </Tooltip>
          );
        })}
      </Box>

      <Typography variant="caption" sx={{ color: palette.inkFaint }}>
        Each stage counts every lead that reached it, including those that moved on, were lost or closed as Other.
        Arrows show the share that went on to the next stage. Other means the lead ended without a win or a loss,
        such as an information request. For leads closed before stage history was recorded, the furthest stage is
        read from the fields filled in, which cannot show Negotiation.
      </Typography>
    </ConsolePanel>
  );
};

export default PipelineRail;
